import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ExternalLink, CheckCircle2, UserRoundCog, RefreshCw, X, Filter, ListFilter, Activity,
} from "lucide-react";
import { ScreenTitle } from "@/components/ScreenTitle";
import { Link } from "react-router-dom";

const CAT_LABEL = {
  personalities: "Personnalités",
  parties: "Organisations",
  institutions: "Institutions",
  themes: "Thèmes",
};

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function fmtRelative(iso) {
  if (!iso) return "jamais";
  const d = new Date(iso);
  const s = Math.round((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `il y a ${s}s`;
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.round(s / 3600)} h`;
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function PoliticsAlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [schedStatus, setSchedStatus] = useState(null);
  const [categoryId, setCategoryId] = useState("");   // "" = all
  const [statusFilter, setStatusFilter] = useState("new");
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [entityFilter, setEntityFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const params = { limit: 200 };
      if (statusFilter !== "all") params.status = statusFilter;
      if (urgentOnly) params.urgent_only = true;
      if (entityFilter.trim()) params.entity = entityFilter.trim();
      if (categoryId) params.category_id = categoryId;
      const [a, s, c, sched] = await Promise.all([
        api.get("/politics/alerts", { params }),
        api.get("/politics/stats"),
        categories.length ? Promise.resolve({ data: categories }) : api.get("/politics/categories"),
        api.get("/politics/scheduler/status"),
      ]);
      setAlerts(a.data);
      setStats(s.data);
      setSchedStatus(sched.data);
      if (!categories.length) setCategories(c.data);
    } catch (e) { /* interceptor */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, urgentOnly, entityFilter, categoryId]);

  const mark = async (id, status) => {
    try {
      await api.patch(`/politics/alerts/${id}`, { status });
      toast.success(status === "handled" ? "Marquée traitée" : "À confier");
      load();
    } catch (e) { toast.error("Action impossible"); }
  };

  const refreshNow = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await api.post("/politics/tick");
      toast.success("Veille relancée sur toutes les catégories");
      setTimeout(load, 4000);
    } catch (e) {
      toast.error("Impossible de relancer la veille");
    } finally {
      setTimeout(() => setRefreshing(false), 4000);
    }
  };

  const catById = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.id] = c;
    return m;
  }, [categories]);

  const uniqueEntities = useMemo(() => {
    const set = new Set();
    for (const a of alerts) {
      for (const cat of Object.keys(a.matches || {})) {
        for (const t of a.matches[cat]) set.add(t);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [alerts]);

  const perCatCounts = stats?.per_category || {};

  return (
    <div data-testid="politics-alerts-page">
      <ScreenTitle
        eyebrow={`Veille · ${stats?.alerts_new ?? "—"} nouvelle(s)`}
        title="Alertes"
        right={
          <div className="flex gap-2">
            <Link
              to="/veille/categories"
              data-testid="go-categories"
              className="min-h-[44px] px-3 flex items-center gap-2 border border-[var(--border-default)] bg-white text-[11px] uppercase tracking-widest font-mono hover:bg-black hover:text-white hover:border-black"
            >
              <ListFilter className="w-4 h-4" strokeWidth={1.75} />
              <span className="hidden sm:inline">Catégories</span>
            </Link>
            <button
              onClick={refreshNow}
              disabled={refreshing}
              data-testid="politics-refresh"
              className="min-h-[44px] px-3 flex items-center gap-2 border border-black bg-black text-white text-[11px] uppercase tracking-widest font-mono hover:bg-[#222] disabled:opacity-60 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.75} />
              <span className="hidden sm:inline">{refreshing ? "…" : "Rafraîchir"}</span>
            </button>
          </div>
        }
      />

      {/* Scheduler status banner */}
      {schedStatus && (
        <div className="px-5 pb-3" data-testid="scheduler-status">
          <div className="border border-[var(--border-default)] bg-white p-3 text-[11px] font-mono uppercase tracking-widest flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <span className="flex items-center gap-1.5 text-[var(--text-muted)]">
              <Activity className="w-3.5 h-3.5" strokeWidth={1.75} />
              Scheduler
            </span>
            <span>
              dernier tick : <b className="text-black normal-case">{fmtRelative(schedStatus.last_tick_at)}</b>
            </span>
            {(schedStatus.per_category || []).map((c) => {
              const totalRead = c.items_read || 0;
              return (
                <span key={c.category_id} className="text-[var(--text-muted)]">
                  · <b className="text-black normal-case">{c.category_name}</b> —
                  {" "}lus {totalRead}
                  {" · "}frais {c.fresh}
                  {" · "}créés {c.created}
                  {c.dedup_grouped ? ` · dédup ${c.dedup_grouped}` : ""}
                  {c.errors ? ` · erreurs ${c.errors}` : ""}
                </span>
              );
            })}
            {(!schedStatus.per_category || schedStatus.per_category.length === 0) && (
              <span className="text-[var(--text-muted)]">En attente du premier tick…</span>
            )}
          </div>
        </div>
      )}

      {/* Category tabs */}
      <div className="px-5 pb-3 flex flex-wrap gap-2 items-center" data-testid="category-tabs">
        <button
          onClick={() => setCategoryId("")}
          data-testid="cat-tab-all"
          className={`min-h-[32px] px-2.5 text-[11px] font-mono uppercase tracking-widest border ${categoryId === "" ? "bg-black text-white border-black" : "border-[var(--border-default)] text-[var(--text-muted)]"}`}
        >
          Toutes · {stats?.alerts_new ?? 0}
        </button>
        {categories.map((c) => {
          const active = categoryId === c.id;
          const count = perCatCounts[c.id]?.new ?? 0;
          const urgent = perCatCounts[c.id]?.urgent ?? 0;
          return (
            <button
              key={c.id}
              onClick={() => setCategoryId(c.id)}
              data-testid={`cat-tab-${c.id}`}
              style={active ? { background: c.color, borderColor: c.color, color: "white" } : { borderColor: c.color, color: c.color }}
              className="min-h-[32px] px-2.5 text-[11px] font-mono uppercase tracking-widest border"
            >
              {c.name} · {count}{urgent ? ` · ${urgent}🔴` : ""}
            </button>
          );
        })}
      </div>

      {/* Sub-filters */}
      <div className="px-5 pb-3 flex flex-wrap gap-2 items-center text-[11px] font-mono uppercase tracking-widest">
        <span className="text-[var(--text-muted)] flex items-center gap-1">
          <Filter className="w-3.5 h-3.5" /> Filtres
        </span>
        <button
          onClick={() => setStatusFilter("new")}
          data-testid="filter-new"
          className={`min-h-[32px] px-2 border ${statusFilter === "new" ? "bg-black text-white border-black" : "border-[var(--border-default)] text-[var(--text-muted)]"}`}
        >Nouvelles</button>
        <button
          onClick={() => setStatusFilter("all")}
          data-testid="filter-all"
          className={`min-h-[32px] px-2 border ${statusFilter === "all" ? "bg-black text-white border-black" : "border-[var(--border-default)] text-[var(--text-muted)]"}`}
        >Toutes</button>
        <button
          onClick={() => setUrgentOnly((v) => !v)}
          data-testid="filter-urgent"
          className={`min-h-[32px] px-2 border ${urgentOnly ? "bg-[var(--accent-strong)] text-white border-[var(--accent-strong)]" : "border-[var(--border-default)] text-[var(--text-muted)]"}`}
        >Urgentes</button>
        <select
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
          data-testid="filter-entity"
          className="min-h-[32px] px-2 border border-[var(--border-default)] text-[var(--text-muted)] bg-white font-mono text-[11px] uppercase tracking-widest"
        >
          <option value="">Toutes entités</option>
          {uniqueEntities.map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
      </div>

      {loading && <div className="px-5 py-10 text-center text-[var(--text-muted)] text-sm">Chargement…</div>}
      {!loading && alerts.length === 0 && (() => {
        const totalNew = stats?.alerts_new ?? 0;
        const total24h = stats?.alerts_last_24h ?? 0;
        const isFilteringActive = (statusFilter !== "all") || urgentOnly ||
          entityFilter.trim() || categoryId;
        // Case A: alerts exist but filter hides them
        if (totalNew > 0 && isFilteringActive) {
          return (
            <div className="px-5 py-12 text-center" data-testid="empty-filter">
              <p className="text-sm">
                <b>{totalNew}</b> alerte(s) existent mais aucune ne correspond à ce filtre.
              </p>
              <button
                onClick={() => {
                  setStatusFilter("all"); setUrgentOnly(false);
                  setEntityFilter(""); setCategoryId("");
                }}
                data-testid="reset-filters"
                className="mt-3 min-h-[36px] px-4 border border-black bg-white hover:bg-black hover:text-white text-[11px] font-mono uppercase tracking-widest"
              >
                Réinitialiser les filtres
              </button>
            </div>
          );
        }
        // Case B: nothing at all in DB but scheduler ran → sources drying up
        const lastTick = schedStatus?.last_tick_at;
        const totalRead = (schedStatus?.per_category || []).reduce((s, c) => s + (c.items_read || 0), 0);
        return (
          <div className="px-5 py-12 text-center text-[var(--text-muted)]" data-testid="empty-nothing">
            <p className="text-sm">Aucune alerte pour l&apos;instant.</p>
            {lastTick ? (
              <p className="text-xs mt-2">
                Dernier tick : {fmtRelative(lastTick)} · {totalRead} item(s) lus au total sur toutes les sources.
              </p>
            ) : (
              <p className="text-xs mt-2">La veille n&apos;a pas encore tourné. Clique sur &quot;Rafraîchir&quot;.</p>
            )}
            {total24h === 0 && totalRead > 0 && (
              <p className="text-xs mt-2 text-black">
                Les sources renvoient bien du contenu, mais rien ne correspond à tes mots-clés
                actuels. Va dans <Link to="/veille/categories" className="underline">Catégories</Link>{" "}
                pour élargir les listes, ou teste un flux via <Link to="/veille/flux" className="underline">Flux</Link>.
              </p>
            )}
          </div>
        );
      })()}

      <ul className="divide-y divide-[var(--border-default)]" data-testid="alerts-list">
        {alerts.map((a) => {
          const cat = catById[a.category_id];
          const groupedCount = (a.grouped_sources || []).length;
          return (
            <li key={a.id} className="px-5 py-4" data-testid="alert-card">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] flex items-center gap-2 flex-wrap">
                {cat && (
                  <span
                    className="px-1.5 py-0.5 text-white text-[10px]"
                    style={{ background: cat.color }}
                    data-testid="alert-category"
                  >{cat.name}</span>
                )}
                {a.urgent && <span className="text-[var(--accent-strong)] font-semibold">● Urgent</span>}
                <span>{a.feed_name}</span>
                <span>·</span>
                <span>{fmtTime(a.detected_at)}</span>
                {groupedCount > 0 && (
                  <>
                    <span>·</span>
                    <span title={a.grouped_sources.map((g) => g.name).join(", ")}>
                      +{groupedCount} source(s)
                    </span>
                  </>
                )}
              </div>
              <h3 className="mt-1.5 font-serif text-lg leading-tight">{a.title}</h3>

              <div className="mt-2 flex flex-wrap gap-1.5">
                {Object.entries(a.matches || {}).map(([cKey, hits]) => (
                  <React.Fragment key={cKey}>
                    {hits.slice(0, 4).map((h) => (
                      <span key={cKey + h}
                            className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 bg-black text-white"
                            title={CAT_LABEL[cKey] || cKey}>
                        {h}
                      </span>
                    ))}
                  </React.Fragment>
                ))}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <a href={a.source_url} target="_blank" rel="noreferrer noopener"
                   data-testid="alert-open"
                   className="min-h-[36px] px-3 flex items-center gap-2 border border-[var(--border-default)] text-[11px] font-mono uppercase tracking-widest hover:bg-black hover:text-white hover:border-black">
                  <ExternalLink className="w-3.5 h-3.5" /> Ouvrir
                </a>
                {a.status !== "handled" && (
                  <button onClick={() => mark(a.id, "handled")} data-testid="alert-handled"
                    className="min-h-[36px] px-3 flex items-center gap-2 border border-[var(--border-default)] text-[11px] font-mono uppercase tracking-widest hover:bg-black hover:text-white hover:border-black">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Traitée
                  </button>
                )}
                {a.status !== "assigned" && (
                  <button onClick={() => mark(a.id, "assigned")} data-testid="alert-assign"
                    className="min-h-[36px] px-3 flex items-center gap-2 border border-[var(--border-default)] text-[11px] font-mono uppercase tracking-widest hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]">
                    <UserRoundCog className="w-3.5 h-3.5" /> À confier
                  </button>
                )}
                {a.status !== "new" && (
                  <span className="min-h-[36px] px-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                    <X className="w-3.5 h-3.5" /> {a.status}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
